
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetraaRDSAgent () {
    return (
        <div>
          <main className="transition-opacity duration-300">
            <div className="py-4">
      
              {/* Breadcrumbs */}
              <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
                <a className="hover:text-primary hover:underline" href="#">
                  Netraa APM
                </a>
                <FiChevronRight />
                <span>Agent Setup Guide</span>
                <FiChevronRight />
                <span>Cloud</span>
                <FiChevronRight />
                <span>AWS</span>
                <FiChevronRight />
                <span className="text-[#1b140d] dark:text-white font-medium">
                  RDS
                </span>
              </nav>
      
              {/* Intro (converted from Hero → match API Gateway style) */}
              <div className="mb-10 scroll-mt-24" id="overview">
                <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                  Documentation
                </span>
      
                <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                    AWS RDS Monitoring – Setup Guide
                  </span>
                </h1>
      
                <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                  This document explains how to enable AWS RDS monitoring and stream CloudWatch metrics to Netraa APM using Amazon Kinesis Data Firehose and CloudWatch Metric Streams.
                </p>
              </div>
      
              {/* Overview */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                    <span className="material-symbols-outlined text-[24px]">insights</span>
                  </span>
                  Overview
                </h2>
      
                <ol className="list-decimal pl-5 space-y-3 text-text-muted dark:text-gray-400">
                  <li>
                    Enable CloudWatch metrics for AWS RDS (default for most RDS engines).
                  </li>
                  <li>
                    Create a Kinesis Data Firehose Delivery Stream and configure an HTTPs Destination pointing to:
                    <CodeBlock code={`https://netraaapm.ap2l.ai/aws/firehose/metrics`} />
                  </li>
                  <li>
                    Create a CloudWatch Metric Stream and route AWS/RDS metrics into the Firehose delivery stream.
                  </li>
                  <li>Select the OpenTelemetry 0.7 output format.</li>
                </ol>
              </section>
      
              {/* Prerequisites */}
              <section className="mb-12 scroll-mt-24" id="prerequisites">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                    <span className="material-symbols-outlined text-[24px]">check_circle</span>
                  </span>
                  Prerequisites
                </h2>
      
                <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>AWS account with permissions:</li>
                  <li>cloudwatch:PutMetricStream</li>
                  <li>firehose:CreateDeliveryStream</li>
                  <li>firehose:PutRecord</li>
                  <li>rds:DescribeDBInstances</li>
                  <li>Existing RDS instance(s)</li>
                  <li>
                    Internet connectivity for Firehose to send data to the Netraa APM HTTPS endpoint
                  </li>
                </ul>
              </section>
      
              {/* Step 1 */}
              <section className="mb-12 scroll-mt-24" id="steps">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
                    <span className="material-symbols-outlined text-[24px]">settings</span>
                  </span>
                  Step 1: Enable CloudWatch Metrics for RDS
                </h2>
      
                <p className="text-text-muted dark:text-gray-400 mb-2">
                  CloudWatch metrics are enabled by default for RDS.
                </p>
      
                <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Go to AWS Console → RDS → Databases</li>
                  <li>Select your DB instance</li>
                  <li>Open the Monitoring tab</li>
                  <li>
                    Confirm that metrics such as CPUUtilization, FreeStorageSpace,
                    ReadLatency, WriteLatency, etc., are available.
                  </li>
                </ol>
      
                <p className="text-text-muted dark:text-gray-400 mt-2">
                  If enhanced monitoring is required:
                </p>
      
                <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Modify the DB instance</li>
                  <li>Enable Enhanced Monitoring</li>
                  <li>Apply changes</li>
                </ol>
              </section>
      
              {/* Step 2 */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                    <span className="material-symbols-outlined text-[24px]">cloud</span>
                  </span>
                  Step 2: Create a Kinesis Data Firehose Delivery Stream
                </h2>
      
                <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Go to AWS Console → Kinesis → Data Firehose</li>
                  <li>Click Create delivery stream</li>
                  <li>
                    Configure:
                    <ul className="list-disc pl-5 mt-1">
                      <li>Source: Direct PUT</li>
                      <li>Destination: HTTP Endpoint</li>
                    </ul>
                  </li>
                  <li>
                    Set HTTP destination settings:
                    <CodeBlock code={`https://netraaapm.ap2l.ai/aws/firehose/metrics`} />
                  </li>
                  <li>Disable data transformation</li>
                  <li>Create the delivery stream</li>
                </ol>
              </section>
      
              {/* Step 3 */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                    <span className="material-symbols-outlined text-[24px]">stream</span>
                  </span>
                  Step 3: Create a CloudWatch Metric Stream
                </h2>
      
                <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Go to AWS Console → CloudWatch → Metric Streams</li>
                  <li>Click Create metric stream</li>
                  <li>Select Output format: OpenTelemetry 0.7</li>
                  <li>Choose namespaces: AWS/RDS</li>
                  <li>Select the Firehose delivery stream created in Step 2</li>
                  <li>
                    Optionally select:
                    <ul className="list-disc pl-5">
                      <li>All RDS instances</li>
                      <li>Specific DB instances</li>
                    </ul>
                  </li>
                  <li>Review and create the metric stream</li>
                </ol>
              </section>
      
              {/* Data Flow */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
                    <span className="material-symbols-outlined text-[24px]">device_hub</span>
                  </span>
                  Data Flow
                </h2>
      
                <CodeBlock code={`AWS RDS → CloudWatch Metrics → CloudWatch Metric Stream → Kinesis Firehose → HTTPS Endpoint → Netraa APM`} />
              </section>
      
              {/* Completion */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-text-main dark:text-white mb-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                    <span className="material-symbols-outlined text-[24px]">done_all</span>
                  </span>
                  Completion
                </h2>
      
                <p className="text-text-muted dark:text-gray-400">
                  After configuration, metrics typically begin streaming into Netraa APM within 30–60 seconds.
                </p>
              </section>
      
            </div>
          </main>
        </div>
      );
    }